import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { CheckCircle2, Mail, Phone, MapPin, ArrowRight, Lock } from 'lucide-react';
import { FormSelect } from '../components/FormSelect';
import { Breadcrumbs } from '../components/Breadcrumbs';
export function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    orgName: '',
    orgType: '',
    country: '',
    city: '',
    sector: '',
    enquiryType: '',
    productCategory: '',
    quantity: '',
    timescale: '',
    budgetHolder: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  return <>
      <PageHero label="Get in Touch" title="Contact Chiltern Global" subtitle="Our team is available to discuss manufacturing requirements, framework procurement, and product specifications." data-id="element-581" />

      <Breadcrumbs items={[{
      label: 'Contact Us'
    }]} data-id="element-582" />

      <section className="bg-white py-24" data-id="element-583">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-584">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-585">
            {/* Contact Info */}
            <div className="lg:col-span-1" data-id="element-586">
              <h2 className="text-2xl font-bold text-navy-900 mb-8" data-id="element-587">
                Contact Information
              </h2>
              <div className="space-y-8" data-id="element-588">
                <div className="flex items-start gap-4" data-id="element-589">
                  <div className="w-10 h-10 bg-slate-50 border border-steel-200 flex items-center justify-center rounded-sm flex-shrink-0" data-id="element-590">
                    <Mail className="w-5 h-5 text-cg-orange-600" data-id="element-591" />
                  </div>
                  <div data-id="element-592">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-593">
                      Email
                    </h3>
                    <p className="text-steel-600" data-id="element-594">
                      enquiries@chilternglobal.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-595">
                  <div className="w-10 h-10 bg-slate-50 border border-steel-200 flex items-center justify-center rounded-sm flex-shrink-0" data-id="element-596">
                    <Phone className="w-5 h-5 text-cg-orange-600" data-id="element-597" />
                  </div>
                  <div data-id="element-598">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-599">
                      Phone
                    </h3>
                    <p className="text-steel-600" data-id="element-600">+44 (0) 1234 567890</p>
                    <p className="text-steel-500 text-sm mt-1" data-id="element-601">
                      Mon-Fri, 09:00 - 17:00 GMT
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-602">
                  <div className="w-10 h-10 bg-slate-50 border border-steel-200 flex items-center justify-center rounded-sm flex-shrink-0" data-id="element-603">
                    <MapPin className="w-5 h-5 text-cg-orange-600" data-id="element-604" />
                  </div>
                  <div data-id="element-605">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-1" data-id="element-606">
                      Head Office
                    </h3>
                    <p className="text-steel-600" data-id="element-607">
                      Unit 12, Industrial Park
                      <br data-id="element-608" />
                      Hertfordshire
                      <br data-id="element-609" />
                      United Kingdom
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-navy-900 rounded-sm text-white" data-id="element-610">
                <h3 className="font-bold mb-2 flex items-center gap-2" data-id="element-611">
                  <Lock className="w-4 h-4 text-cg-orange-600" data-id="element-612" /> Secure &
                  Confidential
                </h3>
                <p className="text-sm text-steel-300 leading-relaxed" data-id="element-613">
                  All enquiries submitted through this form are transmitted via
                  encrypted channels and treated in strict confidence. Your
                  information will not be shared with third parties. For
                  sensitive or classified requirements, please indicate this in
                  your message and we will arrange additional secure
                  communication channels.
                </p>
              </div>
            </div>

            {/* Structured Contact Form */}
            <div className="lg:col-span-2" data-id="element-614">
              {!submitted ? <form onSubmit={handleSubmit} className="bg-slate-50 p-8 md:p-10 border border-steel-200 rounded-sm space-y-10" data-id="element-615">
                  <div data-id="element-616">
                    <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-617">
                      Send an Enquiry
                    </h2>
                    <p className="text-steel-600 mb-8" data-id="element-618">
                      Please complete the form below to route your enquiry to
                      the correct department.
                    </p>
                  </div>

                  {/* Section A: Contact Details */}
                  <div data-id="element-619">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-620">
                      Contact Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-621">
                      <div data-id="element-622">
                        <label htmlFor="firstName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-623">
                          First Name *
                        </label>
                        <input type="text" id="firstName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-624" />
                      </div>
                      <div data-id="element-625">
                        <label htmlFor="lastName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-626">
                          Last Name *
                        </label>
                        <input type="text" id="lastName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-627" />
                      </div>
                      <div data-id="element-628">
                        <label htmlFor="email" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-629">
                          Work Email *
                        </label>
                        <input type="email" id="email" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-630" />
                      </div>
                      <div data-id="element-631">
                        <label htmlFor="phone" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-632">
                          Telephone
                        </label>
                        <input type="tel" id="phone" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.phone} onChange={handleChange} data-id="element-633" />
                      </div>
                      <div className="md:col-span-2" data-id="element-634">
                        <label htmlFor="jobTitle" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-635">
                          Job Title
                        </label>
                        <input type="text" id="jobTitle" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.jobTitle} onChange={handleChange} data-id="element-636" />
                      </div>
                    </div>
                  </div>

                  {/* Section B: Organisation Details */}
                  <div data-id="element-637">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-638">
                      Organisation Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-639">
                      <div className="md:col-span-2" data-id="element-640">
                        <label htmlFor="orgName" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-641">
                          Organisation Name *
                        </label>
                        <input type="text" id="orgName" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.orgName} onChange={handleChange} data-id="element-642" />
                      </div>
                      <div data-id="element-643">
                        <FormSelect id="orgType" label="Organisation Type" required value={formData.orgType} onChange={handleChange} data-id="element-644">
                          <option value="" data-id="element-645">Select Type</option>
                          <option value="Public sector buyer" data-id="element-646">
                            Public sector buyer
                          </option>
                          <option value="Defence buyer" data-id="element-647">Defence buyer</option>
                          <option value="Police force" data-id="element-648">Police force</option>
                          <option value="Custodial/prison service" data-id="element-649">
                            Custodial/prison service
                          </option>
                          <option value="Medical/NGO procurement" data-id="element-650">
                            Medical/NGO procurement
                          </option>
                          <option value="Private sector" data-id="element-651">Private sector</option>
                          <option value="Distributor" data-id="element-652">Distributor</option>
                          <option value="Other" data-id="element-653">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-654">
                        <label htmlFor="country" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-655">
                          Country *
                        </label>
                        <input type="text" id="country" required className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.country} onChange={handleChange} data-id="element-656" />
                      </div>
                      <div data-id="element-657">
                        <label htmlFor="city" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-658">
                          City
                        </label>
                        <input type="text" id="city" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.city} onChange={handleChange} data-id="element-659" />
                      </div>
                    </div>
                  </div>

                  {/* Section C: Enquiry Routing */}
                  <div data-id="element-660">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-661">
                      Enquiry Routing
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-662">
                      <div data-id="element-663">
                        <FormSelect id="sector" label="Sector" required value={formData.sector} onChange={handleChange} data-id="element-664">
                          <option value="" data-id="element-665">Select Sector</option>
                          <option value="Defence" data-id="element-666">Defence</option>
                          <option value="Police" data-id="element-667">Police</option>
                          <option value="Prisons" data-id="element-668">Prisons</option>
                          <option value="Medical" data-id="element-669">Medical</option>
                          <option value="Other" data-id="element-670">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-671">
                        <FormSelect id="enquiryType" label="Enquiry Type" required value={formData.enquiryType} onChange={handleChange} data-id="element-672">
                          <option value="" data-id="element-673">Select Type</option>
                          <option value="Product enquiry" data-id="element-674">
                            Product enquiry
                          </option>
                          <option value="Framework enquiry" data-id="element-675">
                            Framework enquiry
                          </option>
                          <option value="Manufacturing enquiry" data-id="element-676">
                            Manufacturing enquiry
                          </option>
                          <option value="Design and development enquiry" data-id="element-677">
                            Design and development enquiry
                          </option>
                          <option value="Documentation request" data-id="element-678">
                            Documentation request
                          </option>
                          <option value="Catalogue request" data-id="element-679">
                            Catalogue request
                          </option>
                          <option value="Other" data-id="element-680">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-681">
                        <FormSelect id="productCategory" label="Product/Category of Interest" value={formData.productCategory} onChange={handleChange} data-id="element-682">
                          <option value="" data-id="element-683">Select Category (Optional)</option>
                          <option value="Bags and Backpacks" data-id="element-684">
                            Bags and Backpacks
                          </option>
                          <option value="Clothing" data-id="element-685">Clothing</option>
                          <option value="Fire Retardants" data-id="element-686">
                            Fire Retardants
                          </option>
                          <option value="Public Order" data-id="element-687">Public Order</option>
                          <option value="Tactical Vests" data-id="element-688">Tactical Vests</option>
                          <option value="Training Equipment" data-id="element-689">
                            Training Equipment
                          </option>
                          <option value="Other" data-id="element-690">Other</option>
                        </FormSelect>
                      </div>
                      <div data-id="element-691">
                        <FormSelect id="quantity" label="Quantity/Scale" value={formData.quantity} onChange={handleChange} data-id="element-692">
                          <option value="" data-id="element-693">Select Scale (Optional)</option>
                          <option value="Single item" data-id="element-694">Single item</option>
                          <option value="Small batch" data-id="element-695">Small batch</option>
                          <option value="Ongoing supply" data-id="element-696">Ongoing supply</option>
                          <option value="Large procurement" data-id="element-697">
                            Large procurement
                          </option>
                        </FormSelect>
                      </div>
                      <div data-id="element-698">
                        <FormSelect id="timescale" label="Timescale" value={formData.timescale} onChange={handleChange} data-id="element-699">
                          <option value="" data-id="element-700">Select Timescale (Optional)</option>
                          <option value="Immediate" data-id="element-701">Immediate</option>
                          <option value="1 to 4 weeks" data-id="element-702">1 to 4 weeks</option>
                          <option value="1 to 3 months" data-id="element-703">1 to 3 months</option>
                          <option value="3 or more months" data-id="element-704">
                            3 or more months
                          </option>
                        </FormSelect>
                      </div>
                      <div data-id="element-705">
                        <label htmlFor="budgetHolder" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-706">
                          Budget Holder Email
                        </label>
                        <input type="email" id="budgetHolder" className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" value={formData.budgetHolder} onChange={handleChange} data-id="element-707" />
                      </div>
                    </div>
                  </div>

                  {/* Section D: Message */}
                  <div data-id="element-708">
                    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4 border-b border-steel-200 pb-2" data-id="element-709">
                      Message
                    </h3>
                    <div data-id="element-710">
                      <label htmlFor="message" className="block text-sm font-bold text-navy-900 mb-2" data-id="element-711">
                        Message / Requirements *
                      </label>
                      <textarea id="message" required rows={5} className="w-full bg-white border border-steel-300 rounded-sm px-4 py-3 text-navy-900 focus:outline-none focus:border-cg-orange-600" placeholder="Please include specification/configuration, intended use sector, quantity, delivery region, and procurement constraints..." value={formData.message} onChange={handleChange} data-id="element-712" />
                    </div>
                  </div>

                  <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-colors" data-id="element-713">
                    Submit Enquiry
                  </button>

                  <div className="flex items-start gap-2 bg-white border border-steel-200 rounded-sm p-3" data-id="element-714">
                    <Lock className="w-3.5 h-3.5 text-cg-orange-600 mt-0.5 flex-shrink-0" data-id="element-715" />
                    <p className="text-xs text-steel-500 leading-relaxed" data-id="element-716">
                      This form is encrypted and all submissions are treated in
                      strict confidence. Your data is processed in accordance
                      with our{' '}
                      <Link to="/privacy-policy" className="underline hover:text-cg-orange-600" data-id="element-717">
                        Privacy Policy
                      </Link>{' '}
                      and{' '}
                      <Link to="/terms-of-service" className="underline hover:text-cg-orange-600" data-id="element-718">
                        Terms of Service
                      </Link>
                      .
                    </p>
                  </div>
                </form> : <div className="bg-green-50 border border-green-200 p-12 rounded-sm text-center h-full flex flex-col items-center justify-center" data-id="element-719">
                  <CheckCircle2 className="w-16 h-16 text-green-600 mb-6" data-id="element-720" />
                  <h3 className="text-2xl font-bold text-navy-900 mb-4" data-id="element-721">
                    Thank you. Your submission has been received.
                  </h3>
                  <p className="text-steel-700 mb-8 max-w-md mx-auto" data-id="element-722">
                    Your enquiry has been routed to the relevant department. We
                    will review your requirements and be in touch.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4" data-id="element-723">
                    <Link to="/products" className="px-6 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-navy-800 transition-colors" data-id="element-724">
                      View Products
                    </Link>
                    <Link to="/frameworks" className="px-6 py-3 bg-white border border-navy-900 text-navy-900 font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-slate-50 transition-colors" data-id="element-725">
                      View Frameworks
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </section>
    </>;
}